import {TextField} from '@mui/material'
import {DatePicker, LocalizationProvider} from '@mui/x-date-pickers'
import {AdapterMoment} from '@mui/x-date-pickers/AdapterMoment'
import {Moment} from 'moment-timezone'
import {useCallback, useEffect, useState, FC} from 'react'

import {useUikitTranslation} from '../../../common'

import {NumberSelect} from './NumberSelect'

type DateStepperProps = {
  date: Moment
  timezone: string
  dateBound: [Moment, Moment]
  onChange?: (date: Moment) => void
  'data-test-id'?: string
}

export const DateStepper: FC<DateStepperProps> = ({
  date,
  timezone,
  dateBound,
  onChange,
  'data-test-id': dataTestId
}) => {
  const {t} = useUikitTranslation()
  const [selectedDate, _setSelectedDate] = useState(date)
  const [minDate, maxDate] = dateBound
  const [open, setOpen] = useState(false)

  useEffect(() => {
    _setSelectedDate(date)
  }, [date, timezone])

  const setSelectedDate = useCallback(
    (date: Moment) => {
      _setSelectedDate(date)
      if (onChange) {
        onChange(date)
      }
    },
    [onChange]
  )

  const getPreviousDate = useCallback(() => selectedDate.clone().subtract(1, 'day'), [selectedDate])
  const getNextDate = useCallback(() => selectedDate.clone().add(1, 'day'), [selectedDate])

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <NumberSelect
        title={t('dateStepper.label')}
        plusButtonProps={{
          onClick: () => setSelectedDate(getNextDate()),
          disabled: getNextDate().isAfter(maxDate, 'day'),
          'data-test-id': `${dataTestId}-plus`
        }}
        minusButtonProps={{
          onClick: () => setSelectedDate(getPreviousDate()),
          disabled: getPreviousDate().isBefore(minDate, 'day'),
          'data-test-id': `${dataTestId}-minus`
        }}
      >
        <DatePicker
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          value={selectedDate}
          onChange={(newValue) => {
            if (newValue && newValue.isValid()) {
              setSelectedDate(newValue)
            }
          }}
          minDate={minDate}
          maxDate={maxDate}
          inputFormat="DD.MM.YY"
          renderInput={({inputRef, inputProps}) => (
            <TextField
              data-test-id={dataTestId}
              ref={inputRef}
              inputProps={{...inputProps, readOnly: true}}
              onClick={() => setOpen(true)}
              sx={{
                width: '80px',
                backgroundColor: 'transparent',
                margin: 0,

                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none'
                },
                '& .MuiInputBase-input': {
                  padding: 0,
                  cursor: 'pointer',
                  textAlign: 'center'
                }
              }}
            />
          )}
        />
      </NumberSelect>
    </LocalizationProvider>
  )
}
